import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import api from "../../services/api.js";

export default function AddTransactionModal({
  open,
  onClose,
  token,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);

  const [customerSearch, setCustomerSearch] = useState("");
  const [customerSelected, setCustomerSelected] = useState(false);

  const [form, setForm] = useState({
    userid: "",
    invoiceid: "",
    amountin: "",
    fees: 0,
    currency: "IDR", // ✅ default
    description: "", // ✅ new
    rate: 1,
    transid: "",
    refundid: null,
    paymethodid: "",
    datepaid: "",
  });

  // ===============================
  // FETCH CUSTOMERS
  // ===============================
  useEffect(() => {
    if (!open) return;

    const fetchCustomers = async () => {
      try {
        const { data } = await api.get("/customers", {
          params: { limit: 1000 },
          headers: { Authorization: token },
        });
        setCustomers(data.data || []);
      } catch (err) {
        console.error("Fetch customers failed:", err);
      }
    };

    fetchCustomers();
  }, [open, token]);

  // ===============================
  // FETCH INVOICES BY USERID
  // ===============================
  useEffect(() => {
    if (!form.userid) {
      setInvoices([]);
      setForm((prev) => ({ ...prev, invoiceid: "", amountin: "" }));
      return;
    }

    const fetchInvoices = async () => {
      try {
        const { data } = await api.get(
          `/invoice/${form.userid}?status=Unpaid`,
          {
            headers: { Authorization: token },
          }
        );
        setInvoices(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Fetch invoices failed:", err);
      }
    };

    fetchInvoices();
  }, [form.userid, token]);

  // ===============================
  // AUTO SET AMOUNT FROM INVOICE
  // ===============================
  useEffect(() => {
    if (!form.invoiceid) return;

    const selected = invoices.find(
      (inv) => String(inv.id) === String(form.invoiceid)
    );

    if (selected) {
      setForm((prev) => ({
        ...prev,
        amountin: selected.total,
        paymentmethod: selected.paymentmethod || "",
      }));
    }
  }, [form.invoiceid, invoices]);

  // ===============================
  // SUBMIT
  // ===============================
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.userid || !form.invoiceid || !form.amountin) {
      return Swal.fire(
        "Validasi Gagal",
        "Customer, Invoice, dan Amount wajib diisi",
        "warning"
      );
    }

    setLoading(true);

    try {
      await api.post(
        "/transaction",
        {
          userid: Number(form.userid),
          invoiceid: Number(form.invoiceid),
          refundid: null,
          amountin: Number(form.amountin),
          fees: Number(form.fees || 0),
          currency: form.currency, // ✅
          description: form.description || null, // ✅
          rate: Number(form.rate || 1),
          transid: form.transid || null,
          paymethodid: form.paymethodid || null,
          datepaid: form.datepaid || null,
        },
        { headers: { Authorization: token } }
      );

      // console.log(
      //   {
      //     userid: Number(form.userid),
      //     invoiceid: Number(form.invoiceid),
      //     refundid: null,
      //     amountin: Number(form.amountin),
      //     fees: Number(form.fees || 0),
      //     currency: form.currency, // ✅
      //     description: form.description || null, // ✅
      //     rate: Number(form.rate || 1),
      //     transid: form.transid || null,
      //     paymethodid: form.paymethodid || null,
      //     datepaid: form.datepaid || null,
      //   },
      //   { headers: { Authorization: token } }
      // );

      Swal.fire("Berhasil", "Transaksi berhasil ditambahkan", "success");
      onSuccess?.();
      onClose();
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const filteredCustomers = customers.filter((c) =>
    `${c.full_name} ${c.customer_id}`
      .toLowerCase()
      .includes(customerSearch.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-lg bg-white dark:bg-gray-900 p-6 shadow-lg">
        <h2 className="mb-4 text-lg font-semibold text-gray-800 dark:text-gray-100">
          Tambah Transaksi
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* ================= CUSTOMER SEARCH ================= */}
          <div className="relative">
            <input
              type="text"
              placeholder="Cari customer (nama / ID)"
              value={customerSearch}
              disabled={customerSelected}
              onChange={(e) => {
                setCustomerSearch(e.target.value);
                setForm((prev) => ({ ...prev, userid: "" }));
              }}
              className="w-full rounded border px-3 py-2 dark:bg-gray-800 dark:border-gray-700 disabled:opacity-50"
            />

            {customerSearch && !customerSelected && (
              <div className="absolute z-10 mt-1 w-full max-h-48 overflow-auto rounded border bg-white dark:bg-gray-800 shadow">
                {filteredCustomers.length === 0 && (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    Customer tidak ditemukan
                  </div>
                )}

                {filteredCustomers.map((c) => (
                  <div
                    key={c.id}
                    onClick={() => {
                      setForm((prev) => ({ ...prev, userid: c.id }));
                      setCustomerSearch(`${c.full_name} (${c.customer_id})`);
                      setCustomerSelected(true);
                    }}
                    className="cursor-pointer px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <div className="font-medium">{c.full_name}</div>
                    <div className="text-xs text-gray-500">{c.customer_id}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {customerSelected && (
            <button
              type="button"
              onClick={() => {
                setCustomerSelected(false);
                setCustomerSearch("");
                setInvoices([]);
                setForm((prev) => ({ ...prev, userid: "", invoiceid: "" }));
              }}
              className="text-sm text-blue-600 hover:underline"
            >
              Ganti customer
            </button>
          )}

          {/* ================= INVOICE SELECT ================= */}
          <select
            value={form.invoiceid}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, invoiceid: e.target.value }))
            }
            disabled={!form.userid}
            className="w-full rounded border px-3 py-2 dark:bg-gray-800 dark:border-gray-700 disabled:opacity-50"
          >
            <option value="">
              {form.userid ? "Pilih Invoice" : "Pilih customer dulu"}
            </option>
            {invoices.map((inv) => (
              <option key={inv.id} value={inv.id}>
                {inv.id} | {inv.status} | {inv.date} | Rp{" "}
                {Number(inv.total).toLocaleString("id-ID")}
              </option>
            ))}
          </select>

          {/* ================= AMOUNT ================= */}
          <input
            type="number"
            placeholder="Amount"
            value={form.amountin}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, amountin: e.target.value }))
            }
            className="w-full rounded border px-3 py-2 dark:bg-gray-800 dark:border-gray-700"
          />

          {/* ================= Payment Method Id ================= */}
          <select
            className="w-full rounded border px-3 py-2 dark:bg-gray-800 dark:border-gray-700"
            name="paymethodid"
            value={form.paymethodid}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, paymethodid: e.target.value }))
            }
            required
          >
            <option value="">Pilih Metode Pembayaran</option>

            <optgroup label="Skinet">
              <option value="SKI1">Mandiri</option>
              <option value="SKI2">BCA</option>
            </optgroup>

            <optgroup label="Duitku">
              <option value="VC">duitku Visa / Master Card / JCB</option>
              <option value="BC">duitku BCA Virtual Account</option>
              <option value="M2">duitku Mandiri Virtual Account</option>
              <option value="VA">duitku Maybank Virtual Account</option>
              <option value="I1">duitku BNI Virtual Account</option>
              <option value="B1">duitku CIMB Niaga Virtual Account</option>
              <option value="BT">duitku Permata Bank Virtual Account</option>
              <option value="A1">duitku ATM Bersama</option>
              <option value="AG">duitku Bank Artha Graha</option>
              <option value="NC">duitku Bank Neo Commerce/BNC</option>
              <option value="BR">duitku BRIVA</option>
              <option value="S1">duitku Bank Sahabat Sampoerna</option>
              <option value="DM">duitku Danamon Virtual Account</option>
              <option value="BV">duitku BSI Virtual Account</option>
              <option value="FT">duitku Pegadaian/ALFA/Pos</option>
              <option value="IR">duitku Indomaret</option>
              <option value="OV">duitku OVO</option>
              <option value="SA">duitku Shopee Pay Apps</option>
              <option value="LF">duitku LinkAja Apps (Fixed Fee)</option>
              <option value="LA">duitku LinkAja Apps (Percentage Fee)</option>
              <option value="DA">duitku DANA</option>
              <option value="SL">duitku Shopee Pay Account Link</option>
              <option value="OL">duitku OVO Account Link</option>
              <option value="JP">duitku Jenius Pay</option>
              <option value="SP">duitku Shopee Pay (QRIS)</option>
              <option value="NQ">duitku Nobu (QRIS)</option>
              <option value="DQ">duitku Dana (QRIS)</option>
              <option value="GQ">duitku Gudang Voucher (QRIS)</option>
              <option value="SQ">duitku Nusapay (QRIS)</option>
              <option value="DN">duitku Indodana Paylater</option>
              <option value="AT">duitku ATOME</option>
            </optgroup>
          </select>

          {/* ================= DESCRIPTION ================= */}
          <textarea
            placeholder="Deskripsi (opsional)"
            value={form.description}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, description: e.target.value }))
            }
            className="w-full rounded border px-3 py-2 dark:bg-gray-800 dark:border-gray-700"
            rows={3}
          />

          {/* ================= TRANSACTION ID ================= */}
          <input
            placeholder="Transaction ID (opsional)"
            value={form.transid}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, transid: e.target.value }))
            }
            className="w-full rounded border px-3 py-2 dark:bg-gray-800 dark:border-gray-700"
          />

          {/* ================= DATE PAID ================= */}
          <input
            type="datetime-local"
            value={form.datepaid}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, datepaid: e.target.value }))
            }
            className="w-full rounded border px-3 py-2 dark:bg-gray-800 dark:border-gray-700"
          />

          {/* ================= ACTIONS ================= */}
          <div className="flex justify-end gap-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded px-4 py-2 bg-gray-300 dark:bg-gray-700 text-gray-800 dark:text-gray-100"
            >
              Batal
            </button>

            <button
              type="submit"
              disabled={loading}
              className="rounded px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? "Menyimpan..." : "Simpan"}
            </button>
          </div>

          {/* ================= HIDDEN CURRENCY ================= */}
          <input type="hidden" value="IDR" />
        </form>
      </div>
    </div>
  );
}
